// ElementsEngagement — the Starter-plan upsell view for the /elements surface (PE-1624).
//
// Replaces the generic UpgradeLock for non-entitled orgs on the Elements surface: a
// value-led "here's what Elements does for you" page positioned as fundraising boosters.
// A STARTER org reaches it by clicking the (always-visible, PE-1624) Elements nav item;
// the Elements page's in-content entitlement gate renders this instead of the table (the
// page's Header/top bar stays visible above it).
//
// PURELY PRESENTATIONAL — no entitlement logic, no data fetching, no routing. Wiring
// (resolving unlockTier from the engine + routing the CTA to /settings/billing) lives in
// the Elements page (packages/dashboard/src/pages/Elements.js) for the list surface, and
// in App.js's EntitlementGate fail branch for the deeper builder routes. Copy + asset
// paths live in ./elementsEngagementContent.ts.
//
// Styling uses the dashboard's paper/ink/edge/sage tokens to match the Billing page and
// the sibling MarketingEngagement; the deep-sage accent (#4A6B4A) mirrors it. No font is
// set here — the page inherits the dashboard's default face.
//
// PREVIEW CLUSTER LAYOUT (right of the hero text):
//   • Desktop (md+): two rows. Row 1 — Website overlay + Supporters + Add-to-cart side by
//     side; each figure flex-grows from a ZERO basis in proportion to its asset's aspect
//     ratio, so the three frames fill the row edge-to-edge AND render at one shared height,
//     at any cluster width (no fixed pixel heights, no object-cover crop). Row 2 — the
//     fundraising bar (ultra-wide) at 70% of the cluster, centered. Every width is a
//     percentage of the cluster, so the whole composition (bar included) scales as ONE
//     proportional unit; the cluster itself is width-capped so the previews stop growing
//     on very wide viewports / browser zoom-out.
//   • Mobile (< md): Row 1 becomes a horizontal scroll-snap CAROUSEL (overlay + supporters
//     + cart + bar as a 4th slide), with sage position dots; each slide shows its image
//     full-width at its intrinsic aspect. The bar renders in BOTH places and is toggled by
//     responsive classes — desktop shows the centered row-2 rail (`hidden md:flex`), mobile
//     shows the in-carousel slide (`md:hidden`) — so there is one source of truth and no
//     imperative DOM moves.

import React from 'react';
import {
  ChartBarIcon,
  UsersIcon,
  FlagIcon,
  QrCodeIcon,
  LinkIcon,
  WindowIcon,
  ShoppingCartIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  ELEMENTS_ENGAGEMENT_CONTENT,
  type BoosterIconKey,
  type PreviewShot as PreviewShotData,
} from './elementsEngagementContent';

// Maps a booster's icon key (content data) to its Heroicon. Closed over BoosterIconKey,
// so adding a key without a glyph here is a compile error.
const BOOSTER_ICONS: Record<BoosterIconKey, React.ComponentType<{ className?: string }>> = {
  bar: ChartBarIcon,
  users: UsersIcon,
  flag: FlagIcon,
  qr: QrCodeIcon,
  link: LinkIcon,
  overlay: WindowIcon,
  cart: ShoppingCartIcon,
  portal: UserCircleIcon,
};

const PRIMARY_BTN =
  'h-9 rounded-md bg-ink px-4 text-sm font-semibold text-paper-light hover:opacity-90';

// Per-image sizing, as COMPLETE literal class strings (Tailwind's JIT scans source
// statically — interpolated/partial variant classes like `md:${x}` are never generated,
// so every class a variant needs must appear whole in the source).
//
// Every image is WIDTH-driven at its intrinsic aspect ratio: it fills its figure, and the
// figure's width is what the layout controls (aspect-proportional flex factors on the
// desktop row, the fixed carousel slide basis on mobile, the centered 70-percent rail for
// the bar). No fixed pixel heights and no object-cover: the whole composition scales as
// one proportional unit with the cluster width, and images always show fully.
const IMG = {
  overlay: 'block w-full h-auto aspect-[1570/1220]',
  supporters: 'block w-full h-auto aspect-[546/572]',
  cart: 'block w-full h-auto aspect-[426/676]',
  // bar: full width at its ultra-wide aspect (used in both the carousel slide and row-2 rail).
  bar: 'block w-full h-auto aspect-[1362/122]',
} as const;

// Desktop row-1 flex sizing: ZERO basis + flex-grow proportional to each asset's
// width/height ratio. Row width then distributes in exactly that ratio, which makes all
// three rendered image heights EQUAL at any row width (height = width / aspect) while the
// frames fill the row edge-to-edge. On mobile the carousel's fixed slide basis wins and
// grow never engages (the carousel row overflows by design, so there is no free space).
const GROW = {
  overlay: 'md:basis-0 md:grow-[1.287]', // 1570 / 1220
  supporters: 'md:basis-0 md:grow-[0.954]', // 546 / 572
  cart: 'md:basis-0 md:grow-[0.630]', // 426 / 676
} as const;

export interface ElementsEngagementProps {
  /** Tier label that unlocks Elements. Wiring passes `unlockTierLabel(FEATURE.FUNDRAISING_BAR)`. */
  unlockTier?: string;
  /** Primary CTA — routes to `/settings/billing`. */
  onUpgrade?: () => void;
  /** Optional secondary CTA. Currently unused (single-CTA design) but kept so App.js can
   *  keep passing it without a callsite change. */
  onLearnMore?: () => void;
}

function Pill({ unlockTier }: { unlockTier: string }) {
  return <Badge variant="success">Available on the {unlockTier} plan</Badge>;
}

// The browser-style window chrome bar (3 dots) shared by every preview frame.
function FrameChrome() {
  return (
    <div className="flex items-center gap-1.5 border-b border-edge bg-paper-inset px-3 py-2">
      <span className="h-2 w-2 rounded-full bg-edge-input" />
      <span className="h-2 w-2 rounded-full bg-edge-input" />
      <span className="h-2 w-2 rounded-full bg-edge-input" />
    </div>
  );
}

// A single preview: sentence-case caption above a browser-window frame that renders the
// real screenshot. `imgClass` controls how the image sizes inside the frame — always
// full-width at the asset's intrinsic aspect; the figure's width (set via `figureClass`)
// is the single sizing knob. No object-cover anywhere: images always show fully. `slide`
// marks the figure as a carousel slide (`data-slide`) so the dot scroll-tracking can find it.
function LabeledShot({
  shot,
  figureClass = '',
  imgClass,
  slide = false,
}: {
  shot: PreviewShotData;
  figureClass?: string;
  imgClass: string;
  slide?: boolean;
}) {
  return (
    <figure
      className={`m-0 flex min-w-0 flex-col gap-2 ${figureClass}`}
      {...(slide ? { 'data-slide': '' } : {})}
    >
      <figcaption className="text-sm font-semibold text-ink-secondary">{shot.label}</figcaption>
      <div className="overflow-hidden rounded-lg border border-edge bg-white shadow-paper">
        <FrameChrome />
        <img src={shot.src} alt={`${shot.label} preview`} loading="lazy" className={imgClass} />
      </div>
    </figure>
  );
}

// The four positions in the mobile carousel, in swipe order. Used to render the dots and
// to label them for assistive tech.
const CAROUSEL_LABELS = ['Website overlay', 'Supporters', 'Add-to-cart', 'Fundraising bar'];

export default function ElementsEngagement({
  unlockTier = 'Base',
  onUpgrade,
}: ElementsEngagementProps) {
  const content = ELEMENTS_ENGAGEMENT_CONTENT;
  const [overlayShot, supportersShot, cartShot] = [
    content.overlayShot,
    content.sideShots.find((s) => s.kind === 'supporters'),
    content.sideShots.find((s) => s.kind === 'add_to_cart'),
  ];
  const barShot = content.sideShots.find((s) => s.kind === 'bar');

  // Mobile carousel: track which slide is centered so the matching dot reads as current.
  const carouselRef = React.useRef<HTMLDivElement>(null);
  const [activeSlide, setActiveSlide] = React.useState(0);

  const handleCarouselScroll = React.useCallback(() => {
    const row = carouselRef.current;
    if (!row) return;
    const mid = row.scrollLeft + row.clientWidth / 2;
    const slides = Array.from(row.querySelectorAll<HTMLElement>('[data-slide]'));
    let best = 0;
    let bestDist = Infinity;
    slides.forEach((s, i) => {
      const center = s.offsetLeft + s.offsetWidth / 2;
      const dist = Math.abs(center - mid);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    });
    setActiveSlide(best);
  }, []);

  const scrollToSlide = React.useCallback((i: number) => {
    const row = carouselRef.current;
    if (!row) return;
    const slides = Array.from(row.querySelectorAll<HTMLElement>('[data-slide]'));
    slides[i]?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  }, []);

  // Shared frame width in the carousel: 84% of the viewport-ish so the next slide peeks in.
  const slideBasis = 'shrink-0 basis-[84%] snap-center';

  return (
    // One standard framed white panel that fills the page container — same
    // border-edge / bg-white / shadow-sm / rounded-lg treatment as MarketingEngagement
    // and the Settings / Donations content panels, so this reads as the same surface.
    <div className="flex h-full min-h-0 w-full flex-1 flex-col">
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto rounded-lg border border-edge bg-white p-7 shadow-sm">
        {/* HERO row — value text on the LEFT (2/5), the preview cluster on the RIGHT (3/5),
            the SAME grid proportions as MarketingEngagement's hero-with-screenshot layout
            so the two upsell surfaces read identically. Stacks below lg (the previews need
            more width than Marketing's single screenshot, so the split starts at lg not md). */}
        <div className="grid items-center gap-6 lg:grid-cols-5">
          {/* Value text + CTA, vertically centered against the preview cluster. */}
          <div className="lg:col-span-2">
            <Pill unlockTier={unlockTier} />
            <h1 className="mt-3 text-2xl font-extrabold tracking-tight text-ink">{content.title}</h1>
            <p className="mt-2 max-w-[48ch] text-sm text-ink-secondary">{content.pitch}</p>
            <div className="mt-5 flex gap-2.5">
              <Button type="button" className={PRIMARY_BTN} onClick={onUpgrade}>
                Upgrade
              </Button>
            </div>
          </div>

          {/* PREVIEW cluster — the 3/5 grid cell (same proportions as MarketingEngagement's
              screenshot cell). Every child is sized as a percentage of the cluster, so the
              whole composition scales proportionally with the cell and can shrink freely
              (min-w-0) without pushing into the text column. */}
          <div className="flex min-w-0 flex-col gap-4 lg:col-span-3">
            {/* Row 1.
                • md+: overlay + supporters + cart side by side, filling the cluster width
                  with aspect-proportional flex factors so all three share one height.
                • < md: horizontal scroll-snap carousel; the bar is appended as a 4th slide. */}
            <div
              ref={carouselRef}
              onScroll={handleCarouselScroll}
              className={
                'flex gap-3 overflow-x-auto scrollbar-hide snap-x snap-mandatory ' +
                'md:items-start md:gap-4 md:overflow-visible md:snap-none'
              }
            >
              {overlayShot ? (
                <LabeledShot
                  shot={overlayShot}
                  slide
                  figureClass={`${slideBasis} ${GROW.overlay}`}
                  imgClass={IMG.overlay}
                />
              ) : null}
              {supportersShot ? (
                <LabeledShot
                  shot={supportersShot}
                  slide
                  figureClass={`${slideBasis} ${GROW.supporters}`}
                  imgClass={IMG.supporters}
                />
              ) : null}
              {cartShot ? (
                <LabeledShot
                  shot={cartShot}
                  slide
                  figureClass={`${slideBasis} ${GROW.cart}`}
                  imgClass={IMG.cart}
                />
              ) : null}
              {/* Bar as the 4th carousel slide — mobile only (md+ shows the row-2 rail below). */}
              {barShot ? (
                <LabeledShot shot={barShot} slide figureClass={`${slideBasis} md:hidden`} imgClass={IMG.bar} />
              ) : null}
            </div>

            {/* Mobile carousel position dots (hidden md+). */}
            <div className="flex justify-center gap-2 md:hidden" role="tablist" aria-label="Preview navigation">
              {CAROUSEL_LABELS.map((label, i) => (
                <button
                  key={label}
                  type="button"
                  aria-label={label}
                  aria-current={activeSlide === i}
                  onClick={() => scrollToSlide(i)}
                  className={`h-[7px] rounded-full transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage ${
                    activeSlide === i ? 'w-[18px] bg-sage' : 'w-[7px] bg-edge-input'
                  }`}
                />
              ))}
            </div>

            {/* Row 2 (desktop only): the fundraising bar at ~70% width, centered. On mobile
                the bar is the 4th carousel slide above instead. */}
            {barShot ? (
              <LabeledShot shot={barShot} figureClass="hidden md:flex mx-auto w-[70%]" imgClass={IMG.bar} />
            ) : null}
          </div>
        </div>

        {/* Element cards — each element on its own card, under a sentence-case section
            label (same placement/mt-6 as MarketingEngagement's "What you can do"). */}
        <div className="mt-6 text-xs font-semibold tracking-wide text-ink-label">
          Engage donors with
        </div>
        <div className="mt-3 grid gap-3.5 sm:grid-cols-2 lg:grid-cols-3">
          {content.boosters.map((item) => {
            const Icon = BOOSTER_ICONS[item.icon];
            return (
              // Same card as MarketingEngagement's benefit cards, a bit smaller (p-4,
              // h-7 icon): each element gets its own card.
              <div
                key={item.title}
                className="rounded-xl border border-edge bg-paper-light p-4 transition-colors hover:border-sage/40"
              >
                <div className="mb-2 flex items-center gap-2.5">
                  <Icon className="h-7 w-7 flex-shrink-0 text-sage" />
                  <h3 className="text-sm font-semibold text-ink">{item.title}</h3>
                </div>
                <p className="text-[13px] leading-relaxed text-ink-secondary">{item.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
